/* Admin dashboard: aanmeldingen-pijplijn.
   Toont alle aanmeldingen per status + volledige workflow per aanmelding:
   beschikbaarheidscheck → aanbod → contract → onboarding. */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, ClipboardList, Sparkles, Send, Copy, FileText, User, X, Loader2, Calendar, Plus, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { localToday } from "@/lib/format";
import TourSlots from "./aanmeldingen/TourSlots";
import AanbodForm from "./aanmeldingen/AanbodForm";

const STATUS_LABELS = {
  nieuw: "Nieuw",
  in_beoordeling: "In beoordeling",
  beschikbaarheid_check: "Beschikbaarheid gecheckt",
  wachtlijst: "Op wachtlijst",
  aanbod_verstuurd: "Aanbod verstuurd",
  aanbod_geaccepteerd: "Aanbod geaccepteerd",
  aanbod_afgewezen: "Aanbod afgewezen",
  aanbod_verlopen: "Aanbod verlopen",
  contract_verstuurd: "Contract verstuurd",
  contract_getekend: "Contract getekend",
  actieve_plaatsing: "Actieve plaatsing",
  afgewezen: "Afgewezen",
  geannuleerd: "Geannuleerd",
};
const STATUS_TONE = {
  nieuw: "bg-blue-50 text-blue-700 border-blue-200",
  in_beoordeling: "bg-amber-50 text-amber-700 border-amber-200",
  beschikbaarheid_check: "bg-purple-50 text-purple-700 border-purple-200",
  wachtlijst: "bg-neutral-100 text-neutral-700 border-neutral-200",
  aanbod_verstuurd: "bg-indigo-50 text-indigo-700 border-indigo-200",
  aanbod_geaccepteerd: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aanbod_afgewezen: "bg-red-50 text-red-700 border-red-200",
  aanbod_verlopen: "bg-neutral-100 text-neutral-500 border-neutral-200",
  contract_verstuurd: "bg-teal-50 text-teal-700 border-teal-200",
  contract_getekend: "bg-teal-100 text-teal-800 border-teal-300",
  actieve_plaatsing: "bg-emerald-100 text-emerald-800 border-emerald-300",
  afgewezen: "bg-red-100 text-red-800 border-red-300",
  geannuleerd: "bg-neutral-200 text-neutral-600 border-neutral-300",
};
const DAG_LABELS = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

export default function AanmeldingenPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/aanmeldingen");
      setRows(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Haal actieve tenant-slug op voor de publieke aanmeld-URL
    api.get("/tenant/me").then(r => setTenantSlug(r.data?.slug || null))
      .catch(() => setTenantSlug(null));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "actief") return rows.filter(r => !["afgewezen", "geannuleerd", "actieve_plaatsing"].includes(r.status));
    return rows.filter(r => r.status === filter);
  }, [rows, filter]);

  const publicUrl = () => {
    if (!tenantSlug) return null;
    return `${window.location.origin}/aanmelden/${tenantSlug}`;
  };

  const copyToClipboard = async (text, label) => {
    if (!text) { toast.error("Publieke URL nog niet beschikbaar"); return; }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} gekopieerd`);
      } else {
        throw new Error("Clipboard API niet beschikbaar");
      }
    } catch (err) {
      toast.error(`Kopiëren mislukt — kopieer handmatig: ${text}`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Aanmeldingen"
        subtitle="Complete pijplijn: publieke aanmelding → beoordeling → aanbod → contract → plaatsing."
        testid="aanmeldingen-header"
        actions={
          <div className="flex items-center gap-2">
            <TourSlots />
            <Button variant="outline" onClick={() => copyToClipboard(publicUrl(), "Publieke aanmeld-URL")}
              data-testid="copy-public-url-btn" disabled={!tenantSlug}>
              <Copy className="h-4 w-4 mr-1" /> Publieke URL
            </Button>
          </div>
        }
      />

      {/* Status-tegels */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-6">
        <StatusTile label="Nieuw" count={counts.nieuw || 0} active={filter === "nieuw"} onClick={() => setFilter("nieuw")} tone="bg-blue-50 text-blue-700" />
        <StatusTile label="In beoordeling" count={counts.in_beoordeling || 0} active={filter === "in_beoordeling"} onClick={() => setFilter("in_beoordeling")} tone="bg-amber-50 text-amber-700" />
        <StatusTile label="Aanbod verstuurd" count={counts.aanbod_verstuurd || 0} active={filter === "aanbod_verstuurd"} onClick={() => setFilter("aanbod_verstuurd")} tone="bg-indigo-50 text-indigo-700" />
        <StatusTile label="Geaccepteerd" count={counts.aanbod_geaccepteerd || 0} active={filter === "aanbod_geaccepteerd"} onClick={() => setFilter("aanbod_geaccepteerd")} tone="bg-emerald-50 text-emerald-700" />
        <StatusTile label="Wachtlijst" count={counts.wachtlijst || 0} active={filter === "wachtlijst"} onClick={() => setFilter("wachtlijst")} tone="bg-neutral-100 text-neutral-700" />
        <StatusTile label="Alle actief" count={rows.filter(r => !["afgewezen", "geannuleerd", "actieve_plaatsing"].includes(r.status)).length} active={filter === "actief"} onClick={() => setFilter("actief")} tone="bg-neutral-100 text-neutral-800" />
      </div>

      <div className="mb-3 flex items-center gap-2 flex-wrap">
        <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} data-testid="filter-all">Alle ({rows.length})</Button>
        <Button size="sm" variant="outline" onClick={load} data-testid="refresh-btn">Vernieuwen</Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-neutral-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 border border-dashed rounded-lg">
          <ClipboardList className="h-10 w-10 mx-auto mb-2 opacity-40" />
          <div className="text-sm">Geen aanmeldingen in deze categorie.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(r => (
            <AanmeldingCard key={r.id} row={r} onClick={() => setSelected(r.id)} />
          ))}
        </div>
      )}

      {selected && (
        <AanmeldingDetail id={selected} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
}

function StatusTile({ label, count, active, onClick, tone }) {
  return (
    <button onClick={onClick}
      data-testid={`tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`text-left p-3 rounded-lg border transition-all ${active ? "ring-2 ring-[color:var(--sage)] border-[color:var(--sage)]" : "border-neutral-200 hover:border-neutral-300"} ${tone}`}>
      <div className="text-2xl font-display leading-none">{count}</div>
      <div className="text-[10px] uppercase tracking-widest mt-1 opacity-80">{label}</div>
    </button>
  );
}

function AanmeldingCard({ row, onClick }) {
  return (
    <Card onClick={onClick} data-testid={`aanmelding-card-${row.id}`}
      className="p-4 cursor-pointer hover:shadow-md transition-shadow bg-white">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-display text-base">{row.kind_voornaam} {row.kind_achternaam}</div>
          <div className="text-xs text-neutral-500">{new Date(row.kind_geboortedatum).toLocaleDateString('nl-NL')}</div>
        </div>
        <Badge variant="outline" className={`border ${STATUS_TONE[row.status] || ""}`}>
          {STATUS_LABELS[row.status] || row.status}
        </Badge>
      </div>
      <div className="text-xs text-neutral-600 space-y-0.5">
        <div><User className="h-3 w-3 inline mr-1" /> {row.ouder_voornaam} {row.ouder_achternaam}</div>
        <div className="truncate">{row.ouder_email}</div>
        <div>{row.location_name} · start {row.gewenste_startdatum}</div>
      </div>
      <div className="text-[10px] text-neutral-400 mt-2">
        Ingediend {new Date(row.created_at).toLocaleDateString('nl-NL')} · Ref {row.id.slice(0, 8).toUpperCase()}
      </div>
    </Card>
  );
}

function AanmeldingDetail({ id, onClose, onChanged }) {
  const [doc, setDoc] = useState(null);
  const [tab, setTab] = useState("overview");
  const [beschikbaarheid, setBeschikbaarheid] = useState(null);
  const [groups, setGroups] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/aanmeldingen/${id}`);
      setDoc(data);
      setBeschikbaarheid(data.beschikbaarheid_result || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/groups").then(r => setGroups(r.data)).catch(() => {});
    api.get("/medewerkers").then(r => setMedewerkers(r.data)).catch(() => {});
  }, []);

  if (!doc) return null;

  const runBeschikbaarheid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/aanmeldingen/${id}/beschikbaarheid`);
      setBeschikbaarheid(data);
      toast.success(data.any_beschikbaar ? "Beschikbaarheid gecheckt" : "Geen plek — overweeg wachtlijst");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const naarWachtlijst = async () => {
    if (!window.confirm("Verplaats aanmelding naar wachtlijst?")) return;
    try {
      await api.post(`/aanmeldingen/${id}/wachtlijst`);
      toast.success("Op wachtlijst geplaatst");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="aanmelding-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {doc.kind_voornaam} {doc.kind_achternaam}
            <Badge variant="outline" className={`border ${STATUS_TONE[doc.status] || ""}`}>
              {STATUS_LABELS[doc.status] || doc.status}
            </Badge>
          </DialogTitle>
          <DialogDescription>Referentie {id.slice(0, 8).toUpperCase()} · Ingediend {new Date(doc.created_at).toLocaleString('nl-NL')}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overzicht</TabsTrigger>
            <TabsTrigger value="beschikbaarheid" data-testid="tab-beschikbaarheid">Beschikbaarheid</TabsTrigger>
            <TabsTrigger value="aanbod" data-testid="tab-aanbod">Aanbod & Contract</TabsTrigger>
            <TabsTrigger value="historie" data-testid="tab-historie">Historie</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-3">
            <Section title="Kind">
              <Kv k="Naam" v={`${doc.kind_voornaam} ${doc.kind_achternaam}`} />
              <Kv k="Geboortedatum" v={doc.kind_geboortedatum} />
            </Section>
            <Section title="Ouder">
              <Kv k="Naam" v={`${doc.ouder_voornaam} ${doc.ouder_achternaam}`} />
              <Kv k="E-mail" v={doc.ouder_email} />
              <Kv k="Telefoon" v={doc.ouder_telefoon} />
              <Kv k="Adres" v={[doc.ouder_adres, doc.ouder_postcode, doc.ouder_stad].filter(Boolean).join(", ")} />
            </Section>
            {(doc.partner_voornaam || doc.partner_email) && (
              <Section title="Tweede ouder">
                <Kv k="Naam" v={`${doc.partner_voornaam || ""} ${doc.partner_achternaam || ""}`} />
                <Kv k="E-mail" v={doc.partner_email} />
                <Kv k="Telefoon" v={doc.partner_telefoon} />
              </Section>
            )}
            <Section title="Gewenste opvang">
              <Kv k="Locatie" v={doc.location_name} />
              <Kv k="Type" v={doc.opvangsoort} />
              <Kv k="Startdatum" v={doc.gewenste_startdatum} />
              <Kv k="Uren/week" v={doc.uren_per_week} />
              <div className="text-xs mt-1">
                <span className="text-neutral-500">Dagen: </span>
                {(doc.gewenste_dagen || []).map((d, i) => (
                  <span key={i} className="inline-block px-2 py-0.5 rounded bg-neutral-100 mr-1 mb-1">{DAG_LABELS[d.weekday]} · {d.dagdeel}</span>
                ))}
              </div>
            </Section>
            {doc.rondleiding && (
              <Section title="Rondleiding">
                {doc.rondleiding.slot_id
                  ? <Kv k="Ingepland" v={`${doc.rondleiding.datum} · ${doc.rondleiding.tijd_van}–${doc.rondleiding.tijd_tot}`} />
                  : <Kv k="Voorkeur" v={`${doc.rondleiding.voorkeur_datum || ""} ${doc.rondleiding.voorkeur_tijd || ""}`} />}
                {doc.rondleiding.opmerking && <Kv k="Opmerking" v={doc.rondleiding.opmerking} />}
              </Section>
            )}
            {doc.opmerkingen && <Section title="Opmerkingen"><div className="text-sm whitespace-pre-wrap">{doc.opmerkingen}</div></Section>}
            <div className="flex flex-wrap gap-2 pt-2">
              {!["afgewezen", "geannuleerd", "actieve_plaatsing"].includes(doc.status) && (
                <RejectButton id={id} onDone={() => { load(); onChanged(); }} />
              )}
              {["nieuw", "in_beoordeling", "beschikbaarheid_check"].includes(doc.status) && (
                <Button variant="outline" onClick={naarWachtlijst} data-testid="btn-wachtlijst">
                  <ClipboardList className="h-4 w-4 mr-1" /> Naar wachtlijst
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="beschikbaarheid" className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-600">Check welke groepen op de gewenste locatie plek + de juiste leeftijd hebben.</div>
              <Button onClick={runBeschikbaarheid} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="btn-check-beschikbaarheid">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" /> Bereken</>}
              </Button>
            </div>
            {beschikbaarheid && (
              <div>
                <div className="text-xs text-neutral-500 mb-2">
                  Kind {beschikbaarheid.kind_leeftijd_maanden ?? "—"} mnd op {beschikbaarheid.startdatum} · Locatie {beschikbaarheid.location_name}
                </div>
                <div className="space-y-2">
                  {beschikbaarheid.groepen.map(g => (
                    <div key={g.group_id} className={`p-3 rounded border flex items-center justify-between ${g.beschikbaar ? "border-emerald-200 bg-emerald-50" : "border-neutral-200"}`} data-testid={`groep-row-${g.group_id}`}>
                      <div>
                        <div className="font-medium">{g.group_name}</div>
                        <div className="text-xs text-neutral-500">
                          Leeftijd {g.min_age_months}-{g.max_age_months} mnd · Capaciteit {g.capacity} · Bezet {g.bezet} · <strong>Vrij {g.vrij}</strong>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!g.age_fit && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Leeftijd mismatch</Badge>}
                        {g.beschikbaar
                          ? <Badge className="bg-emerald-600">Beschikbaar</Badge>
                          : <Badge variant="outline" className="border-red-300 text-red-700 bg-red-50">Vol</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="aanbod" className="space-y-3">
            {["nieuw", "in_beoordeling", "beschikbaarheid_check"].includes(doc.status) && (
              <AanbodForm id={id} doc={doc} groups={groups} onDone={() => { load(); onChanged(); }} />
            )}
            {doc.status === "aanbod_verstuurd" && (
              <div className="p-4 rounded-md bg-indigo-50 border border-indigo-200 space-y-2" data-testid="aanbod-verstuurd-info">
                <div className="font-medium">Aanbod verstuurd</div>
                <Kv k="Groep" v={doc.aanbod_group_name} />
                <Kv k="Startdatum" v={doc.aanbod_startdatum} />
                <Kv k="Geldig tot" v={String(doc.aanbod_valid_until || "").slice(0, 10)} />
                <div className="text-xs mt-2">
                  Ouder-link: <code className="text-[10px] px-1 py-0.5 bg-white rounded border">/aanbod/{doc.aanbod_token?.slice(0, 12)}…</code>
                  <Button size="sm" variant="ghost" data-testid="copy-aanbod-link-btn" onClick={async () => {
                    const link = `${window.location.origin}/aanbod/${doc.aanbod_token}`;
                    try {
                      if (!navigator.clipboard?.writeText) throw new Error("no clipboard");
                      await navigator.clipboard.writeText(link);
                      toast.success("Link gekopieerd");
                    } catch (err) {
                      toast.error("Kopiëren mislukt — kopieer handmatig");
                    }
                  }}><Copy className="h-3 w-3 mr-1" /> Kopieer</Button>
                </div>
                <Button size="sm" variant="outline" onClick={async () => {
                  if (!window.confirm("Aanbod annuleren en reservering vrijgeven?")) return;
                  try {
                    await api.post(`/aanmeldingen/${id}/annuleer-aanbod`, { reden: "Ingetrokken door beheerder" });
                    toast.success("Aanbod ingetrokken");
                    load(); onChanged();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }} data-testid="annuleer-aanbod-btn">
                  <X className="h-3 w-3 mr-1" /> Aanbod intrekken
                </Button>
              </div>
            )}
            {doc.status === "aanbod_geaccepteerd" && (
              <div className="p-4 rounded-md bg-emerald-50 border border-emerald-200 space-y-3" data-testid="geaccepteerd-info">
                <div className="font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-700" /> Ouder heeft geaccepteerd!</div>
                {doc.aanbod_reactie?.ondertekenaar_naam && (
                  <div className="text-xs">Digitaal getekend door <strong>{doc.aanbod_reactie.ondertekenaar_naam}</strong> op {new Date(doc.aanbod_reactie.timestamp).toLocaleString('nl-NL')}</div>
                )}
                <Button onClick={async () => {
                  setBusy(true);
                  try {
                    const { data } = await api.post(`/aanmeldingen/${id}/genereer-contract`);
                    toast.success("Concept-contract aangemaakt");
                    load(); onChanged();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  setBusy(false);
                }} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="btn-genereer-contract">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 mr-1" /> Genereer contract</>}
                </Button>
              </div>
            )}
            {["contract_verstuurd", "contract_getekend"].includes(doc.status) && (
              <div className="p-4 rounded-md bg-teal-50 border border-teal-200 space-y-3" data-testid="onboard-panel">
                <div className="font-medium">Contract-fase</div>
                <div className="text-xs text-neutral-600">
                  Contract {doc.contract_id?.slice(0, 8).toUpperCase() || "—"} is aangemaakt.
                  Onderteken via de bestaande SignRequest-flow. Ronde onboarding hieronder af zodra ouder heeft getekend.
                </div>
                <OnboardForm id={id} doc={doc} medewerkers={medewerkers} groups={groups} onDone={() => { load(); onChanged(); }} />
              </div>
            )}
            {doc.status === "actieve_plaatsing" && (
              <div className="p-4 rounded-md bg-emerald-100 border border-emerald-300 text-center" data-testid="actief-info">
                <div className="text-emerald-800 font-display text-lg mb-1">
                  <CheckCircle2 className="h-5 w-5 inline mr-1" /> Actieve plaatsing
                </div>
                <div className="text-sm text-emerald-700">Plaatsing is compleet — ouder heeft account, kind is toegewezen aan groep {doc.aanbod_group_name || "—"}.</div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="historie" className="space-y-0">
            <div className="flex justify-end mb-3 gap-2 flex-wrap">
              {doc.aanbod_group_id && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const r = await api.get(`/aanmeldingen/${doc.id}/pdf/aanbod`, { responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                      const a = document.createElement("a");
                      a.href = url;
                      const stamp = doc.status === "aanbod_geaccepteerd" ? "ondertekend" : "concept";
                      a.download = `aanbod-${(doc.id || "").slice(0, 8)}-${stamp}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Aanbod-PDF gedownload");
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="btn-download-aanbod-pdf"
                >
                  <FileText className="h-4 w-4 mr-1.5" />
                  Aanbod-PDF
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const r = await api.get(`/aanmeldingen/${doc.id}/pdf/tijdlijn`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `tijdlijn-${(doc.id || "").slice(0, 8)}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Tijdlijn-PDF gedownload");
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="btn-download-tijdlijn-pdf"
              >
                <FileText className="h-4 w-4 mr-1.5" />
                PDF voor GGD
              </Button>
            </div>
            {/* WEBSTER KOV — visuele audit-tijdlijn */}
            {(() => {
              const hist = (doc.status_history || []).slice().reverse();
              if (!hist.length) return (
                <div className="text-sm text-neutral-500 py-6 text-center" data-testid="historie-empty">
                  Nog geen statuswijzigingen geregistreerd
                </div>
              );
              const sourceMeta = {
                admin:  { label: "Medewerker",  icon: "👤", dot: "bg-sage-100 border-sage-400" },
                ouder:  { label: "Ouder",       icon: "👪", dot: "bg-blue-100 border-blue-400" },
                system: { label: "Systeem",     icon: "⚙︎", dot: "bg-neutral-100 border-neutral-400" },
                cron:   { label: "Automatisch", icon: "⏰", dot: "bg-neutral-100 border-neutral-400" },
              };
              return (
                <div className="relative pl-8 pt-2" data-testid="audit-timeline">
                  {/* Verticale lijn */}
                  <div className="absolute left-[13px] top-4 bottom-4 w-px bg-neutral-200" />
                  {hist.map((h, i) => {
                    const meta = sourceMeta[h.source || "admin"] || sourceMeta.admin;
                    return (
                      <div key={i} className="relative pb-4 last:pb-1"
                            data-testid={`historie-${i}`}>
                        {/* Dot op de lijn */}
                        <div className={`absolute -left-[27px] top-0.5 h-6 w-6 rounded-full border-2 ${meta.dot} flex items-center justify-center text-xs font-semibold z-10`}>
                          {meta.icon}
                        </div>
                        <div className="border border-neutral-200 rounded-md p-2.5 bg-white shadow-sm">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              {h.previous_status && (
                                <>
                                  <Badge variant="outline" className="text-[10px] opacity-60">
                                    {STATUS_LABELS[h.previous_status] || h.previous_status}
                                  </Badge>
                                  <span className="text-neutral-400 text-xs">→</span>
                                </>
                              )}
                              <Badge variant="outline"
                                     className={`border text-[11px] font-medium ${STATUS_TONE[h.status] || ""}`}
                                     data-testid={`historie-${i}-status`}>
                                {STATUS_LABELS[h.status] || h.status}
                              </Badge>
                            </div>
                            <div className="text-[11px] text-neutral-500 tabular-nums" title={h.timestamp}>
                              {new Date(h.timestamp).toLocaleString('nl-NL')}
                            </div>
                          </div>
                          <div className="mt-1.5 text-[11px] flex items-center gap-1.5 text-neutral-600">
                            <span className="font-medium">{meta.label}</span>
                            {h.user_email && <><span>·</span><span className="truncate">{h.user_email}</span></>}
                          </div>
                          {h.notes && (
                            <div className="mt-1.5 text-xs text-neutral-700 border-t border-neutral-100 pt-1.5"
                                 data-testid={`historie-${i}-notes`}>
                              {h.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div className="p-3 rounded-md border border-neutral-200 bg-neutral-50/50">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{title}</div>
      <div className="space-y-1 text-sm">{children}</div>
    </div>
  );
}
function Kv({ k, v }) {
  return <div className="flex gap-3 text-sm"><span className="text-neutral-500 min-w-[110px]">{k}</span><span>{v || "—"}</span></div>;
}

function RejectButton({ id, onDone }) {
  const [open, setOpen] = useState(false);
  const [reden, setReden] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reden.trim()) { toast.error("Reden verplicht"); return; }
    setBusy(true);
    try {
      await api.post(`/aanmeldingen/${id}/afwijzen`, { reden });
      toast.success("Aanmelding afgewezen");
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="text-red-700 border-red-200 hover:bg-red-50" data-testid="btn-afwijzen">
        <XCircle className="h-4 w-4 mr-1" /> Afwijzen
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="afwijs-dialog">
          <DialogHeader><DialogTitle>Aanmelding afwijzen</DialogTitle>
            <DialogDescription>Ouder ontvangt een e-mail met deze reden.</DialogDescription></DialogHeader>
          <Textarea rows={4} value={reden} onChange={e => setReden(e.target.value)} placeholder="Bijv. Geen plek beschikbaar op gewenste startdatum" data-testid="afwijs-reden" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={submit} disabled={busy || !reden.trim()} className="bg-red-600 text-white hover:bg-red-700" data-testid="confirm-afwijs">
              {busy ? "Bezig…" : "Afwijzen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function OnboardForm({ id, doc, medewerkers, groups, onDone }) {
  const [mentor, setMentor] = useState(doc.mentor_medewerker_id || "");
  const [groupId, setGroupId] = useState(doc.aanbod_group_id || "");
  const [busy, setBusy] = useState(false);
  const locGroups = groups.filter(g => g.location_id === doc.location_id);
  const locMedewerkers = medewerkers.filter(m => !doc.location_id || (m.location_ids || []).includes(doc.location_id));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/aanmeldingen/${id}/onboard`, {
        mentor_medewerker_id: mentor || null,
        group_id: groupId || null,
      });
      const invites = data.invites?.actions || [];
      const nieuw = invites.filter(a => a.created_user).length;
      toast.success(`Plaatsing actief · ${nieuw} nieuwe ouder-uitnodiging(en) verstuurd`);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Mentor (medewerker)</Label>
          <Select value={mentor} onValueChange={setMentor}>
            <SelectTrigger data-testid="onboard-mentor"><SelectValue placeholder="Kies mentor" /></SelectTrigger>
            <SelectContent className="bg-white">
              {locMedewerkers.map(m => <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Stamgroep</Label>
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="onboard-group"><SelectValue placeholder="Kies groep" /></SelectTrigger>
            <SelectContent className="bg-white">
              {locGroups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Button onClick={submit} disabled={busy} style={{ background: "var(--sage)" }} className="text-white" data-testid="btn-onboard-finish">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Onboarding afronden + ouder uitnodigen</>}
      </Button>
    </div>
  );
}
